<script setup>
// Lead Workspace — full-page counterpart to the existing LeadDetailsDrawer
// (kept unchanged for Kanban/All Leads), reached from the redesigned "My
// Work" (Verified Leads) list. Same pattern as SalesProjectShow.vue: a thin
// shell + one child component per tab, every tab fed by real,
// already-authorized data (Nexceo::AccessPolicy#can_access_lead? gates
// every request server-side via GET .../nexceo/leads/:id).
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import leadsAPI from 'dashboard/api/nexceo/leads';
import nexceoKanbanStagesAPI from 'dashboard/api/nexceo/kanbanStages';
import { LEAD_WORKSPACE_TABS, leadWorkspaceTabs } from './leadWorkspaceTabs';
import {
  formatBudgetRange,
  formatDate,
  leadInitials,
  priorityMeta,
  verificationMeta,
} from './leadFormatters';

import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import ShareDocumentsModal from '../kanban/ShareDocumentsModal.vue';

import OverviewTab from './tabs/OverviewTab.vue';
import ActivityTab from './tabs/ActivityTab.vue';
import RequirementsTab from './tabs/RequirementsTab.vue';
import PropertyMatchTab from './tabs/PropertyMatchTab.vue';
import SiteVisitTab from './tabs/SiteVisitTab.vue';
import NotesTab from './tabs/NotesTab.vue';
import TasksTab from './tabs/TasksTab.vue';

const route = useRoute();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();

const leadId = computed(() => route.params.id);
const validTabKeys = LEAD_WORKSPACE_TABS.map(t => t.key);
const activeTabKey = computed(() =>
  validTabKeys.includes(route.params.tab) ? route.params.tab : 'overview'
);
const workspaceTabs = computed(() =>
  leadWorkspaceTabs({ id: route.params.id, accountId: accountId.value })
);

const TAB_COMPONENTS = {
  overview: OverviewTab,
  activity: ActivityTab,
  requirements: RequirementsTab,
  'property-match': PropertyMatchTab,
  'site-visit': SiteVisitTab,
  notes: NotesTab,
  tasks: TasksTab,
};
const activeComponent = computed(() => TAB_COMPONENTS[activeTabKey.value]);

const lead = ref(null);
const isLoading = ref(true);
const error = ref(null);
const stages = ref([]);
const showStageMenu = ref(false);
const showMoreMenu = ref(false);

async function loadLead(id) {
  if (!id) return;
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await leadsAPI.show(id);
    lead.value = data;
  } catch (e) {
    error.value =
      e?.response?.status === 403
        ? "You don't have access to this lead."
        : 'Lead not found.';
  } finally {
    isLoading.value = false;
  }
}

async function loadStages() {
  try {
    const { data } = await nexceoKanbanStagesAPI.getAll();
    stages.value = Array.isArray(data) ? data : (data?.stages ?? []);
  } catch {
    // non-critical — stage dropdown just won't populate
  }
}

watch(leadId, id => loadLead(id));
onMounted(() => {
  loadLead(leadId.value);
  loadStages();
});

function reload() {
  loadLead(leadId.value);
}

function back() {
  router.push(accountScopedRoute('nexceo_verified_leads'));
}

const otherStages = computed(() =>
  stages.value.filter(s => s.id !== lead.value?.kanban_stage_id)
);

async function onMoveStage(stageKey) {
  showStageMenu.value = false;
  try {
    await leadsAPI.moveStage(lead.value.id, stageKey);
    useAlert('Stage updated.');
    reload();
  } catch {
    useAlert('Failed to move stage.');
  }
}

function editLead() {
  router.push({
    name: 'nexceo_leads_edit',
    params: { accountId: accountId.value, id: lead.value.id },
    query: { return_to: 'lead_workspace' },
  });
}

async function onMarkLost() {
  showMoreMenu.value = false;
  try {
    await leadsAPI.markLost(lead.value.id, 'Marked lost from Lead Workspace');
    useAlert('Lead marked as lost.');
    reload();
  } catch {
    useAlert('Failed to mark lead as lost.');
  }
}

async function onMarkBooked() {
  showMoreMenu.value = false;
  try {
    await leadsAPI.markBooked(lead.value.id);
    useAlert('Lead marked as booked.');
    reload();
  } catch {
    useAlert('Failed to mark lead as booked.');
  }
}

async function onSendTeamAlert() {
  showMoreMenu.value = false;
  try {
    await leadsAPI.sendTeamAlert(lead.value.id);
    useAlert('Team alert sent.');
  } catch (err) {
    useAlert(err?.response?.data?.message || 'Failed to send team alert.');
  }
}

// Call — real WhatsApp Cloud API calling (Nexceo::Voice::WhatsappProvider),
// reusing the same VoiceCallButton.vue component used elsewhere in the app.
// It renders nothing on its own when the account has no voice-enabled
// inbox, so this never shows a broken button. Falls back to a plain tel:
// link only when the lead has no linked Contact (voice calling requires a
// contact_id) — same honest degradation, not a second calling system.
const callHref = computed(() =>
  lead.value?.phone_number && !lead.value?.contact_id
    ? `tel:${lead.value.phone_number}`
    : null
);

// WhatsApp / Email — real sends via the Share Documents modal
// (Nexceo::DocumentShareService for WhatsApp, Nexceo::LeadDocumentMailer
// for email), not client-side deep links.
const shareModalChannel = ref('whatsapp');
const isShareModalOpen = ref(false);

function openShareModal(channel) {
  shareModalChannel.value = channel;
  isShareModalOpen.value = true;
}

// Property Match tab's shortlist — lifted up here (rather than kept local to
// PropertyMatchTab) so it survives switching to another tab and back within
// the same visit. Still resets on a full page reload; there is no
// lead-level shortlist storage to persist it further, and lead.metadata is
// reserved for AI/system use — see the Lead Workspace session's shortlist
// investigation.
const shortlist = ref([]);
function toggleShortlist(match) {
  const idx = shortlist.value.findIndex(m => m.project_id === match.project_id);
  if (idx >= 0) shortlist.value.splice(idx, 1);
  else shortlist.value.push(match);
}

// Only bound onto <component :is> when it's actually PropertyMatchTab, so
// the other tabs never receive a stray `shortlist` fallthrough attribute.
const propertyMatchExtraProps = computed(() => {
  if (activeTabKey.value !== 'property-match') return {};
  return {
    shortlist: shortlist.value,
    onToggleShortlist: toggleShortlist,
    onShareDocuments: () => openShareModal('whatsapp'),
  };
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    v-if="error"
    class="flex items-center justify-center w-full h-full bg-n-background"
  >
    <div class="text-center max-w-sm">
      <span
        class="i-lucide-shield-alert size-8 text-red-500 mx-auto block mb-2"
      />
      <p class="text-sm text-n-slate-11">{{ error }}</p>
      <button class="mt-3 text-sm text-woot-500 hover:underline" @click="back">
        Back to My Work
      </button>
    </div>
  </div>

  <div
    v-else
    class="flex flex-col w-full h-full overflow-hidden bg-n-background"
  >
    <!-- Header -->
    <div class="flex flex-col border-b border-n-weak bg-n-background shrink-0">
      <div class="flex items-start gap-3 px-4 pt-3 pb-2">
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors shrink-0 mt-0.5"
          title="Back to My Work"
          @click="back"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>

        <template v-if="lead">
          <div
            class="flex items-center justify-center size-11 rounded-full bg-woot-50 dark:bg-woot-500/15 text-woot-600 dark:text-woot-400 font-semibold text-sm shrink-0"
          >
            {{ leadInitials(lead.name) }}
          </div>

          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <h1 class="text-base font-semibold text-n-slate-12 truncate">
                {{ lead.name || 'Unnamed Lead' }}
              </h1>
              <span
                class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-semibold capitalize"
                :class="priorityMeta(lead.priority).cls"
              >
                {{ priorityMeta(lead.priority).label }}
              </span>
              <span
                class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                :class="verificationMeta(lead.verification_status).cls"
              >
                <span
                  class="size-3"
                  :class="verificationMeta(lead.verification_status).icon"
                />
                {{ verificationMeta(lead.verification_status).label }}
              </span>
            </div>
            <div
              class="flex items-center gap-2 mt-0.5 text-xs text-n-slate-10 flex-wrap"
            >
              <span
                v-if="lead.source_channel"
                class="inline-flex items-center gap-1 capitalize"
              >
                <span class="i-lucide-radio-tower size-3" />
                {{ lead.source_channel }}
              </span>
              <span
                v-if="lead.created_at"
                class="inline-flex items-center gap-1"
              >
                <span class="i-lucide-calendar size-3" />
                Created {{ formatDate(lead.created_at) }}
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-hash size-3" />
                Lead ID #{{ lead.id }}
              </span>
              <span v-if="lead.project" class="inline-flex items-center gap-1">
                <span class="i-lucide-building-2 size-3" />
                {{ lead.project.name }}
              </span>
            </div>
          </div>

          <!-- Top actions -->
          <div class="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <VoiceCallButton
              v-if="lead.contact_id"
              :phone="lead.phone_number"
              :contact-id="lead.contact_id"
              label="Call"
              icon="i-lucide-phone"
              size="sm"
              color="blue"
            />
            <a
              v-else-if="callHref"
              :href="callHref"
              class="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors font-medium"
            >
              <span class="i-lucide-phone size-3.5" /> Call
            </a>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              @click="openShareModal('whatsapp')"
            >
              <span class="i-lucide-message-circle size-3.5" /> WhatsApp
            </button>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-n-weak rounded-lg text-n-slate-12 hover:border-woot-400 transition-colors font-medium"
              @click="openShareModal('email')"
            >
              <span class="i-lucide-mail size-3.5" /> Email
            </button>
            <div class="relative">
              <button
                class="flex items-center gap-1 px-3 py-1.5 text-sm border border-n-weak rounded-lg text-n-slate-12 hover:border-woot-400 transition-colors font-medium"
                @click="showMoreMenu = !showMoreMenu"
              >
                <span class="i-lucide-more-horizontal size-3.5" /> More
              </button>
              <div
                v-if="showMoreMenu"
                class="absolute right-0 top-full mt-1 w-48 bg-n-solid-2 border border-n-weak rounded-lg shadow-lg z-20 py-1"
                @click.self="showMoreMenu = false"
              >
                <button
                  class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 flex items-center gap-2"
                  @click="editLead"
                >
                  <span class="i-lucide-pencil size-3.5" /> Edit Lead
                </button>
                <button
                  class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 flex items-center gap-2"
                  @click="onSendTeamAlert"
                >
                  <span class="i-lucide-bell size-3.5" /> Send Team Alert
                </button>
                <button
                  class="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-n-alpha-2 flex items-center gap-2"
                  @click="onMarkBooked"
                >
                  <span class="i-lucide-check-circle size-3.5" /> Mark Booked
                </button>
                <button
                  class="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-n-alpha-2 flex items-center gap-2"
                  @click="onMarkLost"
                >
                  <span class="i-lucide-x-circle size-3.5" /> Mark Lost
                </button>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="h-11 flex-1 rounded bg-n-alpha-2 animate-pulse" />
      </div>

      <!-- Stat strip -->
      <div
        v-if="lead"
        class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 px-4 pb-3"
      >
        <div class="relative rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">
            Stage
          </p>
          <button
            class="text-sm font-medium text-n-slate-12 flex items-center gap-1 hover:text-woot-600"
            @click="showStageMenu = !showStageMenu"
          >
            {{ lead.kanban_stage?.name || '—' }}
            <span class="i-lucide-chevron-down size-3" />
          </button>
          <div
            v-if="showStageMenu"
            class="absolute left-0 top-full mt-1 w-48 max-h-64 overflow-auto bg-n-solid-2 border border-n-weak rounded-lg shadow-lg z-20 py-1"
          >
            <button
              v-for="s in otherStages"
              :key="s.id"
              class="w-full text-left px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="onMoveStage(s.key)"
            >
              {{ s.name }}
            </button>
          </div>
        </div>
        <div class="rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">
            Priority
          </p>
          <p class="text-sm font-medium text-n-slate-12 capitalize">
            {{ lead.priority }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">
            Lead Score
          </p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ lead.lead_score ?? 0 }}/100
          </p>
        </div>
        <div class="rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">
            Next Follow-up
          </p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ formatDate(lead.next_follow_up_at) || '—' }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">
            Assigned To
          </p>
          <p class="text-sm font-medium text-n-slate-12 truncate">
            {{ lead.assigned_agent?.name || 'Unassigned' }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">
            Expected Value
          </p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ formatBudgetRange(lead) || '—' }}
          </p>
        </div>
      </div>

      <!-- Tab bar -->
      <div
        class="flex items-center gap-0 px-4 border-t border-n-weak overflow-x-auto"
      >
        <button
          v-for="tab in workspaceTabs"
          :key="tab.key"
          type="button"
          class="flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
          :class="
            tab.key === activeTabKey
              ? '!border-woot-500 text-woot-600 dark:text-woot-400'
              : '!border-transparent text-n-slate-9 hover:text-n-slate-12'
          "
          @click="router.push(tab.to)"
        >
          <span class="size-3.5" :class="tab.icon" />
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 min-h-0 overflow-auto flex flex-col">
      <component
        :is="activeComponent"
        v-if="!isLoading && lead"
        :lead="lead"
        v-bind="propertyMatchExtraProps"
        @reload="reload"
      />
      <div v-else class="flex items-center justify-center flex-1">
        <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
      </div>
    </div>

    <ShareDocumentsModal
      :is-open="isShareModalOpen"
      :lead="lead"
      :channel="shareModalChannel"
      @close="isShareModalOpen = false"
      @shared="reload"
    />
  </div>
</template>
